import { useEffect, useState } from "react";
import { useAdmin } from "../contexts/AdminContext";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CopyPrincipal } from "../components/CopyPrincipal";
import { ErrorBanner } from "../components/ErrorBanner";
import { formatDate, indexErrorMessage } from "../lib/utils";
import type { PendingShop } from "../lib/types";

export function PendingPage() {
  const { actor } = useAdmin();
  const [shops, setShops] = useState<PendingShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approving, setApproving] = useState<bigint | null>(null);
  const [confirmIdx, setConfirmIdx] = useState<bigint | null>(null);

  async function load() {
    if (!actor) return;
    try {
      const res: { ok?: PendingShop[]; err?: unknown } = await actor.listPendingShops();
      if ("ok" in res) setShops(res.ok!);
      else setError("Failed to load pending shops");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [actor]); // eslint-disable-line react-hooks/exhaustive-deps

  async function approve(idx: bigint) {
    if (!actor) return;
    setConfirmIdx(null);
    setApproving(idx);
    setError("");
    try {
      const res: { ok?: unknown; err?: unknown } = await actor.approveShop(idx);
      if ("err" in res) setError(indexErrorMessage(res.err));
      else await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to approve");
    } finally {
      setApproving(null);
    }
  }

  const pendingToConfirm = confirmIdx !== null
    ? shops.find((s) => s.idx === confirmIdx)
    : null;

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 24 }}>Pending Shops</h1>

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      {loading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : shops.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No pending shops.</p>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Shop Details</th>
                <th>Owner</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>M-Pesa Ref</th>
                <th>Reg. Code</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shops.map((shop) => {
                const isAwaiting = "AwaitingApproval" in shop.status;
                const isApprovingThis = approving === shop.idx;
                return (
                  <tr key={String(shop.idx)}>
                    <td className="mono" style={{ color: "var(--color-muted)" }}>
                      {String(shop.idx)}
                    </td>
                    <td style={{ minWidth: 220, maxWidth: 320 }}>
                      <div style={{ fontWeight: 500 }}>{shop.name}</div>
                      {shop.contacts[0] && (
                        <div style={shopDetailLineStyle}>{shop.contacts[0]}</div>
                      )}
                      {shop.address[0] && (
                        <div style={shopDetailLineStyle}>{shop.address[0]}</div>
                      )}
                    </td>
                    <td><CopyPrincipal principal={shop.owner.toText()} /></td>
                    <td style={{ color: "var(--color-muted)", fontSize: 13, whiteSpace: "nowrap" }}>
                      {formatDate(shop.createdAt)}
                    </td>
                    <td>
                      <StatusPill awaiting={isAwaiting} />
                    </td>
                    <td>
                      {shop.mpesaRef[0] ? (
                        <span className="mono" style={{ fontSize: 12, color: "var(--color-muted)" }}>
                          {shop.mpesaRef[0]}
                        </span>
                      ) : (
                        <span style={{ color: "var(--color-muted)", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td>
                      {shop.registrationCode[0] ? (
                        <span className="mono" style={{ fontSize: 12, color: "var(--color-muted)" }}>
                          {shop.registrationCode[0]}
                        </span>
                      ) : (
                        <span style={{ color: "var(--color-muted)", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-primary"
                        style={{ fontSize: 13, height: 32, minHeight: 32, padding: "0 14px" }}
                        disabled={!isAwaiting || isApprovingThis}
                        onClick={() => setConfirmIdx(shop.idx)}
                        title={!isAwaiting ? "Payment reference not yet submitted" : "Approve this shop"}
                      >
                        {isApprovingThis ? "Approving…" : "Approve"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmIdx !== null}
        title="Approve Shop"
        message={pendingToConfirm
          ? `Approve "${pendingToConfirm.name}" for ${pendingToConfirm.owner.toText().slice(0, 16)}…? This will spawn a new shop canister (3T cycles).`
          : ""}
        confirmLabel="Approve"
        onConfirm={() => confirmIdx !== null && approve(confirmIdx)}
        onCancel={() => setConfirmIdx(null)}
      />
    </div>
  );
}

const shopDetailLineStyle: React.CSSProperties = {
  color: "var(--color-muted)",
  fontSize: 12,
  lineHeight: 1.45,
  marginTop: 3,
  overflowWrap: "anywhere",
};

function StatusPill({ awaiting }: { awaiting: boolean }) {
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: 4,
      background: awaiting ? "rgba(0,232,122,0.12)" : "rgba(138,158,149,0.12)",
      color: awaiting ? "var(--color-accent)" : "var(--color-muted)",
      border: `1px solid ${awaiting ? "rgba(0,232,122,0.3)" : "rgba(138,158,149,0.2)"}`,
    }}>
      {awaiting ? "Awaiting Approval" : "Pending"}
    </span>
  );
}
